import React, { Component } from 'react';
import propTypes from 'prop-types';
import { connect } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { Card, CardContent, Columns, Column, MuiTitle } from 'src/ui/mui-ui-kit';;
import { find, get } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Field } from 'react-final-form';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import createDecorator from 'final-form-calculate';

import { getUser } from 'src/user';
import {
  setBreadcrumbs,
  SaveChangesToast,
  ConfirmChangesModal,
  InnerLoader,
  UiFieldset,
  UiSelectAdapter,
  UiInputAdapter,
  UiQuillAdapter,
  reduceServerErrors,
  UiToastAdapter,
  maskPhone,
  AddressPickerAdapter,
} from 'src/ui';
import { usStates } from 'src/core';
import { ZIP_MASK, PHONE_MASK } from 'src/ui/const';
import { fetchFormData, addAccountVendor, getFormData } from 'src/account';

import { composeValidators, url } from 'src/ui/validations';
import { addVendor, updateVendor, getVendors } from '../redux';
import { getVendor } from '../services';


const formCalculator = createDecorator({
  field: 'autocompleteAddress',
  updates: {
    primaryAddress: value => get(value, 'address'),
    primaryCity: value => get(value, 'city'),
    primaryState: value => find(usStates, { value: get(value, 'state') }),
    primaryZipcode: value => get(value, 'zipCode'),
  },
});

class VendorEditPage extends Component {
  state = {
    loading: true,
    title: '',
    initialValues: {},
    confirmChangesModal: false,
  };

  homePath = '/vendors';

  constructor(props) {
    super(props);

    if (props.isAllVendors) {
      this.homePath = '/all-vendors';
    }
  }

  async componentDidMount() {
    const { match, setBreadcrumbs, fetchFormData } = this.props;
    const { id } = match.params;

    const title = id ? 'Edit' : 'Add';

    this.setState({ title });

    setBreadcrumbs([
      { label: 'Vendors', href: this.homePath },
      { label: title, href: match.url },
    ]);

    const { user } = this.props;

    if (user.financialInstitutionId || user.isSuperAdmin) {
      await fetchFormData(user.financialInstitutionId, user.accountId);

      if (id) {
        await this.buildData(+id);
      }
    }

    this.setState({ loading: false });
  }

  handleSubmit = async values => {
    const { match } = this.props;
    const { id } = match.params;

    if (id) {
      return this.updateVendor(id, values);
    }
    return this.addVendor(values);
  };

  showModal = () => {
    this.toggleModal(true);
  };

  hideModal = () => {
    this.toggleModal(false);
  };

  async addVendor(values) {
    const { addVendor } = this.props;

    const result = await addVendor({
      ...values,
      primaryState: get(values, 'primaryState.value'),
      registeredState: get(values, 'registeredState.value'),
      businessEntityType: get(values, 'businessEntityType.id'),
      isApproved: false,
      isDenied: false,
      isArchived: false,
    });

    const { vendors } = this.props;

    if (vendors.isError()) {
      return reduceServerErrors(vendors);
    }

    const vendorId = result.payload.id;

    const { addAccountVendor, user } = this.props;

    if (user.accountId && !user.isBankAdmin) {
      addAccountVendor({
        accountId: user.accountId,
        vendorId,
      });
    }

    toast(UiToastAdapter('Vendor added successfully!', 'success'));
    return undefined;
  }

  async updateVendor(id, values) {
    const { updateVendor } = this.props;

    await updateVendor({
      vendorId: id,
      ...values,
      primaryState: get(values, 'primaryState.value'),
      registeredState: get(values, 'registeredState.value'),
      businessEntityType: get(values, 'businessEntityType.id'),
    });

    const { vendors } = this.props;

    if (vendors.isError()) {
      return reduceServerErrors(vendors);
    }
    toast(UiToastAdapter('Vendor updated successfully!', 'success'));
    return undefined;
  }

  async buildData(id) {
    const {
      formData: { businessEntityTypes },
    } = this.props;

    const vendorResponse = await getVendor(id);
    const vendor = vendorResponse.data;

    if (vendor) {
      const initialValues = {
        ...vendor,
        businessEntityType: find(businessEntityTypes, {
          id: vendor.businessEntityType,
        }),
        primaryState: find(usStates, { value: vendor.primaryState }),
        registeredState: find(usStates, { value: vendor.registeredState }),
        primaryPhone: vendor.primaryPhone && maskPhone(vendor.primaryPhone),
        otherPhone: vendor.otherPhone && maskPhone(vendor.otherPhone),
      };
      this.setState({ initialValues });
    }
  }

  toggleModal(value) {
    this.setState({ confirmChangesModal: value });
  }

  render() {
    const { isExpanded, formData } = this.props;
    const { title, loading, initialValues, confirmChangesModal } = this.state;

    const toastContainerClassName = classNames('bottom-toast-container', {
      'is-condensed': !isExpanded,
    });

    return (
      <>
        <MuiTitle isSize="4" hasTextColor="info" tag="h2">
          {`${title} Vendor`}
          <NavLink
            className="close-page button is-small is-text is-pulled-right"
            to={this.homePath}
          >
            <span className="icon has-text-danger">
              <FontAwesomeIcon icon={['fal', 'times-circle']} size="2x" />
            </span>
          </NavLink>
        </MuiTitle>
        <Form
          onSubmit={this.handleSubmit}
          decorators={[formCalculator]}
          subscription={{
            submitting: true,
            dirty: true,
            hasValidationErrors: true,
            dirtySinceLastSubmit: true,
          }}
          initialValues={initialValues}
          render={({
            handleSubmit,
            submitting,
            hasValidationErrors,
            dirty,
            dirtySinceLastSubmit,
          }) => (
            <Card tag="form" onSubmit={handleSubmit}>
              <CardContent>
                <div className={toastContainerClassName}>
                  <SaveChangesToast
                    message="Click the button to the right to save your changes"
                    isLoading={submitting}
                    isDisabled={submitting || hasValidationErrors}
                    isVisible={dirty || dirtySinceLastSubmit}
                    onClickButton={this.showModal}
                  />
                </div>
                <ConfirmChangesModal
                  title="Vendor"
                  isActive={confirmChangesModal}
                  onClickCancel={this.hideModal}
                  onClickSubmit={this.hideModal}
                />
                <InnerLoader visible={submitting || loading} />
                <UiFieldset title="General Information" isFirst>
                  <Columns isMultiline>
                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiSelectAdapter}
                        name="businessEntityType"
                        id="businessEntityType"
                        required
                        options={formData.businessEntityTypes}
                        label="Business Entity Type"
                        getOptionLabel={item => item.name}
                        getOptionValue={item => item.id}
                      />
                    </Column>
                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="businessName"
                        id="businessName"
                        required
                        label="Business Name"
                      />
                    </Column>

                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="ssn"
                        id="ssn"
                        label="EIN/TIN/SSN"
                      />
                    </Column>
                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="naics"
                        id="naics"
                        label="NAICS Code"
                      />
                    </Column>
                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="businessEmail"
                        id="businessEmail"
                        label="Email Address"
                        type="email"
                      />
                    </Column>

                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="primaryPhone"
                        id="primaryPhone"
                        label="Primary Phone"
                        renderIcons={() => [
                          <span
                            className="icon is-small is-right"
                            key="right-icon"
                          >
                            <FontAwesomeIcon icon={['fal', 'phone']} />
                          </span>,
                        ]}
                        hasIcons="right"
                        mask={PHONE_MASK}
                        guide={false}
                        type="tel"
                      />
                    </Column>
                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="otherPhone"
                        id="otherPhone"
                        label="Other Phone"
                        renderIcons={() => [
                          <span
                            className="icon is-small is-right"
                            key="right-icon"
                          >
                            <FontAwesomeIcon icon={['fal', 'phone']} />
                          </span>,
                        ]}
                        hasIcons="right"
                        mask={PHONE_MASK}
                        guide={false}
                        type="tel"
                      />
                    </Column>

                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="website"
                        id="website"
                        validate={composeValidators('Website', url)}
                        label="Website"
                        renderIcons={() => [
                          <span
                            className="icon is-small is-right"
                            key="right-icon"
                          >
                            <FontAwesomeIcon icon={['fal', 'link']} />
                          </span>,
                        ]}
                        hasIcons="right"
                      />
                    </Column>

                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiSelectAdapter}
                        name="registeredState"
                        id="registeredState"
                        options={usStates}
                        label="Registered State"
                        getOptionLabel={item => (item ? item.label : '')}
                        getOptionValue={item => (item ? item.value : null)}
                      />
                    </Column>
                  </Columns>
                </UiFieldset>

                <UiFieldset title="Location Address">
                  <Columns isMultiline>
                    <Column isSize="full">
                      <Field
                        component={AddressPickerAdapter}
                        label="Search Address"
                        name="autocompleteAddress"
                        id="autocompleteAddress"
                      />
                    </Column>

                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="primaryAddress"
                        id="primaryAddress"
                        label="Primary Address"
                      />
                    </Column>
                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="primaryAddress2"
                        id="primaryAddress2"
                        label="Primary Address 2"
                      />
                    </Column>

                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="primaryCity"
                        id="primaryCity"
                        label="Primary City"
                      />
                    </Column>
                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiInputAdapter}
                        name="primaryZipcode"
                        id="primaryZipcode"
                        label="Primary Zip Code"
                        mask={ZIP_MASK}
                      />
                    </Column>

                    <Column isSize="1/2" className="is-one-third-fullhd">
                      <Field
                        component={UiSelectAdapter}
                        name="primaryState"
                        id="primaryState"
                        options={usStates}
                        label="Primary State"
                      />
                    </Column>
                  </Columns>
                </UiFieldset>

                <UiFieldset title="Goods &amp; Services Provided">
                  <Field
                    component={UiQuillAdapter}
                    name="goodsAndServicesProvided"
                    id="goodsAndServicesProvided"
                    placeholder="Please list goods and services provided *"
                  />
                </UiFieldset>
              </CardContent>
            </Card>
          )}
        />
      </>
    );
  }
}

VendorEditPage.defaultProps = {
  isAllVendors: false,
};

VendorEditPage.propTypes = {
  match: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  vendors: propTypes.object.isRequired,
  formData: propTypes.object.isRequired,
  setBreadcrumbs: propTypes.func.isRequired,
  addVendor: propTypes.func.isRequired,
  updateVendor: propTypes.func.isRequired,
  fetchFormData: propTypes.func.isRequired,
  isExpanded: propTypes.bool.isRequired,
  addAccountVendor: propTypes.func.isRequired,
  isAllVendors: propTypes.bool,
};

function mapStateToProps(state) {
  return {
    user: getUser(state),
    formData: getFormData(state),
    vendors: getVendors(state),
  };
}

const connectedVendorEditPage = connect(mapStateToProps, {
  setBreadcrumbs,
  addVendor,
  updateVendor,
  fetchFormData,
  addAccountVendor,
})(VendorEditPage);

export { connectedVendorEditPage as VendorEditPage };
